// The draft's formatting, written as Markdown: headings, lists, checklists, quotes, code, dividers,
// tables, bold, italics, strikethrough, and links, with underline, subscript, superscript, colors,
// and highlights as the HTML tags Markdown takes for them. The draft stays plain text, every character of it where it
// was typed, so what's sent, compared, undone, and kept is the text itself; this says which
// characters are formatting, and how the rest reads, for the editor and the versions to show.

import { highlightOf, textColorOf, type Highlight, type TextColor } from './colors';

/** What a line of the draft is. */
export type LineKind =
  | 'paragraph'
  | 'heading'
  | 'bullet'
  | 'ordered'
  | 'task'
  | 'quote'
  /** A divider across the text: ---, or *** or ___. */
  | 'rule'
  /** A row of a table: | its | cells |, the first of them its header. */
  | 'row'
  /** The line under a table's header, | --- | --- |, which only says it's a table. */
  | 'delimiter'
  /** A line of ``` that starts or ends a code block. */
  | 'fence'
  /** A line in a code block, taken as it's written. */
  | 'code';

/** Formatting within a line. */
export type MarkKind =
  | 'strong'
  | 'em'
  | 'underline'
  | 'strike'
  | 'sub'
  | 'sup'
  | 'code'
  | 'link'
  | 'color'
  | 'highlight';

/** A stretch of a line with formatting of one kind, such as **bold**. */
export interface Span {
  readonly kind: MarkKind;
  /** The whole of it, from its first marker to the end of its last. */
  readonly start: number;
  readonly end: number;
  /** Its text, between the markers. */
  readonly from: number;
  readonly to: number;
  /** Where a link goes. */
  readonly href?: string;
  /** The color words are in, or highlighted with. */
  readonly color?: TextColor | Highlight;
}

/** A table cell's text, between the pipes and the spaces around it. */
export interface Cell {
  readonly from: number;
  readonly to: number;
}

export interface Line {
  readonly kind: LineKind;
  readonly start: number;
  /** Where its text starts, after what makes it a heading, list item, or quote, such as "- ". */
  readonly from: number;
  /** Where it ends, before its line break. */
  readonly end: number;
  /** A heading's level, from 1 to 6. */
  readonly level: number;
  /** How deep a list item is nested, from 0. */
  readonly depth: number;
  /** A numbered item's number as it's written, such as "1." or "2)". */
  readonly number: string;
  /** A checklist item that's ticked. */
  readonly checked: boolean;
  /** A fence that starts a code block, rather than ending one. */
  readonly opens: boolean;
  /** The formatting within it, each span before those inside it. */
  readonly spans: readonly Span[];
  /** Backslashes that only say the character after them is meant as it is, such as \*. */
  readonly escapes: readonly number[];
  /** A table row's cells. */
  readonly cells: readonly Cell[];
  /** A table's first row, which heads its columns. */
  readonly header: boolean;
  /** How many columns a row's table has, as its header says. */
  readonly columns: number;
}

// Inline formatting starts and ends against text, not spaces; punctuation counts as neither.
const SPACE = /\s/u;
const PUNCTUATION = /[\p{P}\p{S}]/u;
const ESCAPABLE = /[!-/:-@[-`{-~]/u;
// The deepest a list nests, as the editor indents it.
const MAX_DEPTH = 6;

const FENCE = /^ {0,3}(`{3,}|~{3,})(.*)$/u;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/u;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+|$)/u;
const QUOTE = /^ {0,3}> ?/u;
const TASK = /^([ \t]*)[-*+][ \t]+\[([ xX])\](?:[ \t]+|$)/u;
const BULLET = /^([ \t]*)[-*+][ \t]+/u;
const ORDERED = /^([ \t]*)(\d{1,9}[.)])[ \t]+/u;
const ROW = /^ {0,3}\|/u;
const DELIMITER = /^ {0,3}\|(?:[ \t]*:?-+:?[ \t]*\|)*[ \t]*:?-+:?[ \t]*\|?[ \t]*$/u;

/** How far in a line's indentation reaches, with tabs to the next multiple of four. */
function columns(indent: string): number {
  let width = 0;
  for (const char of indent) width = char === '\t' ? width + 4 - (width % 4) : width + 1;
  return width;
}

/** The draft's lines, each with what it is and the formatting within it. */
export function parseMarkdown(text: string): Line[] {
  const lines: Line[] = [];
  let fence: { readonly char: string; readonly size: number } | null = null;
  // The table the lines are in: how many columns it has, and whether its delimiter is next.
  let table: { readonly columns: number; readonly delimiter: boolean } | null = null;
  // The list items a new one could be nested in: where each one's text starts.
  let parents: number[] = [];
  let start = 0;
  const sources = text.split('\n');
  sources.forEach((source, index) => {
    const end = start + source.length;
    const line = (kind: LineKind, from: number, extra: Partial<Line> = {}): Line => ({
      ...BLANK,
      kind,
      start,
      from,
      end,
      ...extra,
    });

    const fenced = FENCE.exec(source);
    if (fence) {
      const closes =
        fenced?.[1]?.[0] === fence.char && fenced[1].length >= fence.size && !fenced[2]?.trim();
      lines.push(line(closes ? 'fence' : 'code', start));
      if (closes) fence = null;
    } else if (table?.delimiter) {
      lines.push(line('delimiter', end));
      table = { columns: table.columns, delimiter: false };
    } else if (table && ROW.test(source)) {
      lines.push(rowLine(text, start, end, false, table.columns));
    } else if (ROW.test(source) && isHeader(text, start, end, sources[index + 1])) {
      // A table starts with its header, then the line that says it's one.
      const header = rowLine(text, start, end, true, 0);
      const columns = header.cells.length;
      lines.push({ ...header, columns });
      table = { columns, delimiter: true };
      parents = [];
    } else if (fenced?.[1] && !(fenced[1].startsWith('`') && fenced[2]?.includes('`'))) {
      table = null;
      fence = { char: fenced[1][0] ?? '`', size: fenced[1].length };
      lines.push(line('fence', start, { opens: true }));
      parents = [];
    } else {
      table = null;
      const block = blockOf(source, start, parents);
      if ((block.kind === 'paragraph' && source.trim()) || block.kind === 'rule') parents = [];
      const inline = inlineOf(text, block.from ?? start, end);
      lines.push(line(block.kind, block.from ?? start, { ...block, ...inline }));
    }
    start = end + 1;
  });
  return lines;
}

const BLANK: Omit<Line, 'kind' | 'start' | 'from' | 'end'> = {
  level: 0,
  depth: 0,
  number: '',
  checked: false,
  opens: false,
  spans: [],
  escapes: [],
  cells: [],
  header: false,
  columns: 0,
};

/** Whether a row is a table's header: the line after it says so, with a column for each cell. */
function isHeader(text: string, start: number, end: number, next: string | undefined): boolean {
  if (next === undefined || !DELIMITER.test(next)) return false;
  return cellsOf(next, 0, next.length).length === cellsOf(text, start, end).length;
}

/**
 * A row's cells: the text between its pipes, without the spaces before it and the one space
 * after it. More spaces after are the cell's own, so a space typed after its words stays to type
 * the next word after. An empty cell is where its text would go once typed, after a space.
 */
export function cellsOf(text: string, start: number, end: number): Cell[] {
  const cells: Cell[] = [];
  const trimmed = (from: number, to: number): Cell => {
    let first = from;
    let last = to;
    while (first < last && (text[first] === ' ' || text[first] === '\t')) first += 1;
    if (last > first && (text[last - 1] === ' ' || text[last - 1] === '\t')) last -= 1;
    if (first === last) first = last = Math.min(from + 1, to);
    return { from: first, to: last };
  };
  const pipe = text.indexOf('|', start);
  if (pipe === -1 || pipe >= end) return cells;
  let from = pipe + 1;
  for (let at = from; at < end; at += 1) {
    const char = text[at];
    if (char === '\\') at += 1;
    else if (char === '|') {
      cells.push(trimmed(from, at));
      from = at + 1;
    }
  }
  // After the last pipe, there's a cell only where something's written.
  if (text.slice(from, end).trim()) cells.push(trimmed(from, end));
  return cells;
}

/** A table's row, with the formatting in each of its cells. */
export function rowLine(
  text: string,
  start: number,
  end: number,
  header: boolean,
  columns: number,
): Line {
  const cells = cellsOf(text, start, end);
  const spans: Span[] = [];
  const escapes: number[] = [];
  for (const cell of cells) scan(text, cell.from, cell.to, spans, escapes, true);
  // A pipe in a cell is written \|, in code too, where the backslash is all that's escaped.
  for (const span of spans) {
    if (span.kind !== 'code') continue;
    for (let at = span.from; at < span.to - 1; at += 1)
      if (text[at] === '\\' && text[at + 1] === '|') escapes.push(at);
  }
  spans.sort((a, b) => a.start - b.start || b.end - a.end);
  return {
    ...BLANK,
    kind: 'row',
    start,
    from: cells[0]?.from ?? end,
    end,
    spans,
    escapes,
    cells,
    header,
    columns,
  };
}

/** What a line outside a code block is, from how it starts. */
function blockOf(
  source: string,
  start: number,
  parents: number[],
): Partial<Line> & { readonly kind: LineKind } {
  // All of it says it's a divider, so none of it reads.
  if (RULE.test(source)) return { kind: 'rule', from: start + source.length };
  const heading = HEADING.exec(source);
  if (heading?.[1]) {
    return { kind: 'heading', from: start + heading[0].length, level: heading[1].length };
  }
  const quote = QUOTE.exec(source);
  if (quote) return { kind: 'quote', from: start + quote[0].length };

  const task = TASK.exec(source);
  const bullet = task ? null : BULLET.exec(source);
  const ordered = task || bullet ? null : ORDERED.exec(source);
  const item = task ?? bullet ?? ordered;
  if (!item) return { kind: 'paragraph' };
  // Nested in the items above whose text it's indented as far as, or further.
  const indent = columns(item[1] ?? '');
  while (parents.length > 0 && indent < (parents.at(-1) ?? 0)) parents.pop();
  const depth = Math.min(parents.length, MAX_DEPTH);
  parents.push(columns(item[0]));
  const from = start + item[0].length;
  if (task) return { kind: 'task', from, depth, checked: task[2] !== ' ' };
  if (ordered) return { kind: 'ordered', from, depth, number: ordered[2] ?? '' };
  return { kind: 'bullet', from, depth };
}

/** A run of the same emphasis character, which may open formatting, close it, or both. */
interface Delimiter {
  readonly char: string;
  start: number;
  length: number;
  /** Its length as written, for the rule that keeps ***a** b* reading the way it should. */
  readonly size: number;
  readonly opens: boolean;
  readonly closes: boolean;
}

/** Bold, italics, underline, strikethrough, code, and links between `from` and `to`. */
function inlineOf(text: string, from: number, to: number) {
  const spans: Span[] = [];
  const escapes: number[] = [];
  scan(text, from, to, spans, escapes, true);
  spans.sort((a, b) => a.start - b.start || b.end - a.end);
  return { spans, escapes };
}

function scan(
  text: string,
  from: number,
  to: number,
  spans: Span[],
  escapes: number[],
  links: boolean,
): void {
  const delimiters: Delimiter[] = [];
  let at = from;
  while (at < to) {
    const char = text[at] ?? '';
    if (char === '\\' && at + 1 < to && ESCAPABLE.test(text[at + 1] ?? '')) {
      escapes.push(at);
      at += 2;
      continue;
    }
    if (char === '`') {
      const size = runLength(text, at, to, '`');
      const close = closingTicks(text, at + size, to, size);
      if (close !== -1)
        spans.push({ kind: 'code', start: at, from: at + size, to: close, end: close + size });
      at = close === -1 ? at + size : close + size;
      continue;
    }
    if (char === '<') {
      const tag = tagAt(text, at, to);
      if (tag) {
        spans.push(tag);
        scan(text, tag.from, tag.to, spans, escapes, links);
        at = tag.end;
        continue;
      }
    }
    if (char === '[' && links) {
      const link = linkAt(text, at, to);
      if (link) {
        spans.push(link);
        // Its words can be formatted too, though not as another link.
        scan(text, link.from, link.to, spans, escapes, false);
        at = link.end;
        continue;
      }
    }
    if (char === '*' || char === '_' || char === '~') {
      const size = runLength(text, at, to, char);
      // One tilde is "about", as in ~5 minutes; two strike text through.
      if (char !== '~' || size === 2) delimiters.push(delimiter(text, at, size, from, to));
      at += size;
      continue;
    }
    at += 1;
  }
  emphasis(delimiters, spans);
}

function runLength(text: string, at: number, to: number, char: string): number {
  let end = at;
  while (end < to && text[end] === char) end += 1;
  return end - at;
}

/** Where the run of exactly `size` backticks that closes a code span starts, or -1. */
function closingTicks(text: string, from: number, to: number, size: number): number {
  let at = text.indexOf('`', from);
  while (at !== -1 && at < to) {
    const length = runLength(text, at, to, '`');
    if (length === size) return at;
    at = text.indexOf('`', at + length);
  }
  return -1;
}

/** A link, [its words](where it goes), starting at `at`. */
function linkAt(text: string, at: number, to: number): Span | null {
  let depth = 0;
  let close = -1;
  for (let index = at + 1; index < to; index += 1) {
    const char = text[index];
    if (char === '\\') index += 1;
    else if (char === '`') {
      const size = runLength(text, index, to, '`');
      const end = closingTicks(text, index + size, to, size);
      index = end === -1 ? index + size - 1 : end + size - 1;
    } else if (char === '[') depth += 1;
    else if (char === ']') {
      if (depth === 0) {
        close = index;
        break;
      }
      depth -= 1;
    }
  }
  if (close <= at + 1 || text[close + 1] !== '(') return null;
  let parens = 0;
  for (let index = close + 2; index < to; index += 1) {
    const char = text[index] ?? '';
    if (SPACE.test(char)) return null;
    if (char === '(') parens += 1;
    else if (char === ')') {
      if (parens === 0) {
        const href = text.slice(close + 2, index);
        return href
          ? { kind: 'link', start: at, from: at + 1, to: close, end: index + 1, href }
          : null;
      }
      parens -= 1;
    }
  }
  return null;
}

// Underline, subscript, superscript, colors, and highlights, which Markdown has no marks of its
// own for.
const TAGS: Readonly<Record<string, MarkKind>> = { u: 'underline', sub: 'sub', sup: 'sup' };
const TAG = /<(\/?)(u|sub|sup|mark|span)(\s[^<>]*)?>/iuy;
const STYLE = /^\s*style\s*=\s*(?:"([^"]*)"|'([^']*)')\s*$/iu;

/** What an opening tag makes its words: <u> underlined, <span style="color: red"> red. */
function tagKind(name: string, attributes: string): Pick<Span, 'kind' | 'color'> | null {
  const plain = TAGS[name];
  if (plain) return attributes.trim() ? null : { kind: plain };
  const style = STYLE.exec(attributes);
  if (!style)
    return name === 'mark' && !attributes.trim() ? { kind: 'highlight', color: 'yellow' } : null;
  const css = style[1] ?? style[2] ?? '';
  const property = (pattern: RegExp) => pattern.exec(css)?.[1]?.trim() ?? '';
  const color = property(/(?:^|;)\s*color\s*:\s*([^;]+)/iu);
  const background = property(/(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/iu);
  if (name === 'span' && color) {
    const value = textColorOf(color);
    return value ? { kind: 'color', color: value } : null;
  }
  if (background || name === 'mark') {
    const value = background ? highlightOf(background) : 'yellow';
    return value ? { kind: 'highlight', color: value } : null;
  }
  return null;
}

/** A tag and its words, as <u>these</u>, starting at `at`, and closed in the same line. */
function tagAt(text: string, at: number, to: number): Span | null {
  TAG.lastIndex = at;
  const open = TAG.exec(text);
  const name = open?.[2]?.toLowerCase() ?? '';
  if (!open || open[1] || at + open[0].length > to) return null;
  const kind = tagKind(name, open[3] ?? '');
  if (!kind) return null;
  const from = at + open[0].length;
  let depth = 0;
  for (let index = from; index < to; index += 1) {
    const char = text[index];
    if (char === '\\') index += 1;
    else if (char === '`') {
      const size = runLength(text, index, to, '`');
      const end = closingTicks(text, index + size, to, size);
      index = end === -1 ? index + size - 1 : end + size - 1;
    } else if (char === '<') {
      TAG.lastIndex = index;
      const tag = TAG.exec(text);
      if (!tag || tag[2]?.toLowerCase() !== name || index + tag[0].length > to) continue;
      if (!tag[1]) depth += 1;
      else if (tag[3]) continue;
      else if (depth > 0) depth -= 1;
      else
        return index > from
          ? { ...kind, start: at, from, to: index, end: index + tag[0].length }
          : null;
    }
  }
  return null;
}

/** A run of emphasis characters, and whether it can open formatting, close it, or both. */
function delimiter(text: string, at: number, size: number, from: number, to: number): Delimiter {
  const char = text[at] ?? '';
  const before = at > from ? (text[at - 1] ?? ' ') : ' ';
  const after = at + size < to ? (text[at + size] ?? ' ') : ' ';
  const spaceBefore = SPACE.test(before);
  const spaceAfter = SPACE.test(after);
  const punctuationBefore = PUNCTUATION.test(before);
  const punctuationAfter = PUNCTUATION.test(after);
  const left = !spaceAfter && (!punctuationAfter || spaceBefore || punctuationBefore);
  const right = !spaceBefore && (!punctuationBefore || spaceAfter || punctuationAfter);
  // Underscores within a word, as in snake_case, are just underscores.
  const opens = char === '_' ? left && (!right || punctuationBefore) : left;
  const closes = char === '_' ? right && (!left || punctuationAfter) : right;
  return { char, start: at, length: size, size, opens, closes };
}

/** Pairs the runs that open formatting with those that close it, innermost first. */
function emphasis(delimiters: readonly Delimiter[], spans: Span[]): void {
  const openers: Delimiter[] = [];
  for (const closer of delimiters) {
    while (closer.closes && closer.length > 0) {
      let index = openers.length - 1;
      for (; index >= 0; index -= 1) {
        const opener = openers[index];
        if (opener?.char !== closer.char) continue;
        const both = opener.closes || closer.opens;
        if (
          closer.char !== '~' &&
          both &&
          (opener.size + closer.size) % 3 === 0 &&
          !(opener.size % 3 === 0 && closer.size % 3 === 0)
        )
          continue;
        break;
      }
      const opener = openers[index];
      if (!opener) break;
      const use = closer.char === '~' ? 2 : opener.length >= 2 && closer.length >= 2 ? 2 : 1;
      const from = opener.start + opener.length;
      spans.push({
        kind: closer.char === '~' ? 'strike' : use === 2 ? 'strong' : 'em',
        start: from - use,
        from,
        to: closer.start,
        end: closer.start + use,
      });
      opener.length -= use;
      closer.start += use;
      closer.length -= use;
      // Runs between the two can no longer pair with anything outside them.
      openers.length = opener.length > 0 ? index + 1 : index;
    }
    if (closer.opens && closer.length > 0) openers.push(closer);
  }
}

/** The line that `offset` is in, or at the end of. */
export function lineAt(lines: readonly Line[], offset: number): Line {
  let low = 0;
  let high = lines.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if ((lines[middle]?.start ?? 0) <= offset) low = middle;
    else high = middle - 1;
  }
  const line = lines[low];
  if (!line) throw new Error('A parsed draft always has a line.');
  return line;
}

/** Which characters of a line only say how the rest reads, as [start, end) ranges in order. */
export function hiddenRanges(line: Line): [number, number][] {
  if (line.kind === 'code' || line.kind === 'fence') return [];
  const ranges: [number, number][] = [];
  if (line.kind === 'row') {
    // Its pipes, and the spaces around them.
    let at = line.start;
    for (const cell of line.cells) {
      ranges.push([at, cell.from]);
      at = cell.to;
    }
    ranges.push([at, line.end]);
  } else if (line.from > line.start) ranges.push([line.start, line.from]);
  for (const span of line.spans) ranges.push([span.start, span.from], [span.to, span.end]);
  for (const escape of line.escapes) ranges.push([escape, escape + 1]);
  return ranges.filter(([start, end]) => end > start).sort((a, b) => a[0] - b[0]);
}

/**
 * The code blocks that are closed, each as its lines: its fences, and its code between them. A
 * fence still being typed, with no fence below it yet, starts none.
 */
export function codeBlocks(lines: readonly Line[]): Line[][] {
  const blocks: Line[][] = [];
  let current: Line[] | null = null;
  for (const line of lines) {
    if (current) {
      current.push(line);
      if (line.kind === 'fence') {
        blocks.push(current);
        current = null;
      }
    } else if (line.kind === 'fence') current = [line];
  }
  return blocks;
}

/** The formatting on the character at `offset`, by the spans whose text it's in. */
export function marksAt(line: Line, offset: number): Span[] {
  return line.spans.filter((span) => span.from <= offset && offset < span.to);
}

/** Whether the draft has any formatting at all, rather than being plain text. */
export function hasFormatting(text: string): boolean {
  if (!/[*_~`[#><\-+\d\\]/u.test(text)) return false;
  return parseMarkdown(text).some(
    (line) => line.kind !== 'paragraph' || line.spans.length > 0 || line.escapes.length > 0,
  );
}

/**
 * The words alone, as they read, without what formats them: for a voice to read aloud. Code
 * blocks keep their lines; the fences around them go.
 */
export function plainText(text: string): string {
  if (!hasFormatting(text)) return text;
  const lines = parseMarkdown(text);
  const kept: string[] = [];
  for (const line of lines) {
    if (line.kind === 'fence' || line.kind === 'delimiter') continue;
    const hidden = hiddenRanges(line);
    const words = (from: number, to: number) => {
      let out = '';
      let at = from;
      for (const [start, end] of hidden) {
        if (end <= from || start >= to) continue;
        if (start > at) out += text.slice(at, start);
        at = Math.max(at, end);
      }
      return out + text.slice(at, to);
    };
    // A table's cells, one after another, as they'd be read.
    kept.push(
      line.kind === 'row'
        ? line.cells
            // Without spaces left after the words, while the next is typed.
            .map((cell) => words(cell.from, cell.to).trimEnd())
            .filter(Boolean)
            .join(', ')
        : words(line.start, line.end),
    );
  }
  return kept.join('\n');
}

/**
 * The text from `start` to `end` as it reads, without what formats it, line under line: as the
 * words a version took out are shown. A table's cells are a space apart.
 */
export function readableSlice(
  text: string,
  start: number,
  end: number,
  lines: readonly Line[] = parseMarkdown(text),
): string {
  // The first line that reaches `start`.
  let first = 0;
  let last = lines.length - 1;
  while (first < last) {
    const middle = Math.floor((first + last) / 2);
    if ((lines[middle]?.end ?? 0) < start) first = middle + 1;
    else last = middle;
  }
  const read: string[] = [];
  for (const line of lines.slice(first)) {
    if (line.start > end) break;
    if (line.kind === 'fence' || line.kind === 'delimiter') continue;
    const to = Math.min(end, line.end);
    let at = Math.max(start, line.start);
    let out = '';
    let between = false;
    const keep = (words: string) => {
      if (!words) return;
      out += between && out && !/\s$/u.test(out) ? ` ${words}` : words;
      between = false;
    };
    for (const [from, until] of hiddenRanges(line)) {
      if (until <= at || from >= to) continue;
      keep(text.slice(at, from));
      if (line.kind === 'row' && text.slice(from, until).includes('|')) between = true;
      at = Math.max(at, until);
    }
    keep(text.slice(at, to));
    read.push(out);
  }
  return read.join('\n');
}
